import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Countdown } from './Countdown';
import { DarkTexture } from './DarkTexture';
import { BTN_PRIMARY_GLOW, BTN_OUTLINE_GLOW } from '../lib/buttonStyles';

const lineVariants = {
  hidden: { y: '110%' },
  show: { y: '0%' },
};

const MaskedLine = ({ children, delay = 0, className = '' }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial="hidden"
      animate="show"
      variants={lineVariants}
      transition={{ duration: 0.9, delay, ease: [0.33, 1, 0.68, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const KineticHero = () => (
  <section data-testid="home-hero" className="relative bg-navy text-white min-h-screen flex flex-col justify-center overflow-hidden pt-24">
    <DarkTexture />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-cyan mb-8"
        data-testid="hero-kicker"
      >
        Digital Personal Data Protection Act, 2023 · DPDP Rules, 2025
      </motion.p>

      <h1 className="font-serif font-semibold text-[13vw] sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight mb-10">
        <MaskedLine delay={0.1}>India's data law,</MaskedLine>
        <MaskedLine delay={0.25}>explained in</MaskedLine>
        <MaskedLine delay={0.4} className="italic bg-gradient-to-r from-cyan via-indiagreen to-cyan bg-clip-text text-transparent">plain language.</MaskedLine>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.7 }}
        className="max-w-xl text-white/70 text-base sm:text-lg leading-relaxed mb-10"
      >
        dpdpa.blog breaks down every chapter of the DPDPA and the DPDP Rules, 2025 — obligations of Data Fiduciaries, rights of Data Principals, consent, breach notification and penalties — and exactly what your organisation must do before 13 May 2027.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85, duration: 0.7 }}
        className="flex flex-wrap gap-4 mb-16"
      >
        <Link
          to="/dpdpa-act"
          data-testid="hero-cta-read-act"
          className={BTN_PRIMARY_GLOW}
        >
          Read the Act, section by section
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/compliance-assessment"
          data-testid="hero-cta-assessment"
          className={BTN_OUTLINE_GLOW}
        >
          Take the Compliance Assessment
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.7 }}
        className="border-t border-white/10 pt-8 flex flex-col sm:flex-row sm:items-center gap-6"
      >
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Full-compliance deadline</p>
        <Countdown />
      </motion.div>
    </div>
  </section>
);
